import React from 'react';
import { Grid } from '@mui/material';
import DataCard from './DataCard';

const INITIAL_CARD_STATE = {
  totalIncomes: { current: 0, previous: 0 },
  totalExpenses: { current: 0, previous: 0 },
  indirectExpenses: { current: 0, previous: 0 },
  netProfitPercentage: { current: 0, previous: 0 },
  grossProfit: { current: 0, previous: 0 },
  tradepayables: { current: 0, previous: 0 },
  tradereceivables: { current: 0, previous: 0 },
  cashInHand: { current: 0, previous: 0 },
  closingStock: { current: 0, previous: 0 },
  currentassets: { current: 0, previous: 0 },
  currentliabilities: { current: 0, previous: 0 },
  netCurrentAssets: { current: 0, previous: 0 },
};

const GROUP_NAMES = {
  TRADING_INCOMES: 'Trading Incomes',
  DIRECT_INCOME: 'Direct Income',
  TRADING_EXPENSE: 'Trading Expense',
  DIRECT_EXPENSE: 'Direct Expense',
  CASH_IN_HAND: 'Cash in Hand',
  BANK_ACCOUNTS: 'Bank Accounts',
};

const GROUP_NAME_TO_KEY_MAP = {
  'Trade Receivables': 'tradereceivables',
  TradePayables: 'tradepayables',
  [GROUP_NAMES.CASH_IN_HAND]: 'cashInHand',
  [GROUP_NAMES.BANK_ACCOUNTS]: 'cashInHand',
  'Current Assets': 'currentassets',
  'Current Liabilities': 'currentliabilities',
  'Trade Payables': 'tradepayables',
  [GROUP_NAMES.TRADING_INCOMES]: 'totalIncomes',
  [GROUP_NAMES.DIRECT_INCOME]: 'totalIncomes',
  [GROUP_NAMES.TRADING_EXPENSE]: 'totalExpenses',
  [GROUP_NAMES.DIRECT_EXPENSE]: 'totalExpenses',
  'Indirect Expenses': 'indirectExpenses',
  'Depreciation & Amortisation': 'indirectExpenses',
  'Interest Expenses': 'indirectExpenses',
  'Tax Expenses': 'indirectExpenses',
};

const GROUPED_DATA = [
  [
    { key: 'totalIncomes', title: 'Sales & Direct Income' },
    { key: 'totalExpenses', title: 'Purchase & Direct Expense' },
    { key: 'indirectExpenses', title: 'Indirect Expense' },
  ],
  [
    { key: 'grossProfit', title: 'Gross Profit' },
    { key: 'netProfit', title: 'Net Profit' },
    { key: 'closingStock', title: 'Closing Stock' },
  ],
  [
    { key: 'tradepayables', title: 'Account Payables' },
    { key: 'tradereceivables', title: 'Account Receivables' },
    { key: 'cashInHand', title: 'Cash & Bank' },
  ],
  [
    { key: 'currentassets', title: 'Current Assets' },
    { key: 'currentliabilities', title: 'Current Liabilities' },
    { key: 'netCurrentAssets', title: 'Net Current Assets' },
  ],
];

const ComparisonCard = React.memo(({ currentData = [], previousData = [], loading, currentstockSummary, previousStockSummary }) => {

  const calculateSum = React.useCallback((dataArray, groupNames) => {
    if (!dataArray?.length) return 0;
    return dataArray
      .filter((item) => groupNames.includes(item.group_name))
      .reduce((sum, item) => sum + (item.balance || 0), 0);
  }, []);

  const stockData = React.useMemo(() => {
    const currentClosingStock = currentData?.[0]?.closingstockamt || 0;
    const previousClosingStock = previousData?.[0]?.closingstockamt || 0;
    const currentOpeningStock = currentData?.[0]?.openingstockamt || 0;
    const previousOpeningStock = previousData?.[0]?.openingstockamt || 0;

    return {
      currentClosingStock,
      previousClosingStock,
      currentOpeningStock,
      previousOpeningStock,
    };
  }, [currentData, previousData]);

  const incomeExpenseData = React.useMemo(() => {
    const grossIncomeGroups = [GROUP_NAMES.TRADING_INCOMES, GROUP_NAMES.DIRECT_INCOME];
    const grossExpenseGroups = [GROUP_NAMES.TRADING_EXPENSE, GROUP_NAMES.DIRECT_EXPENSE];

    const netIncomeGroups = [GROUP_NAMES.TRADING_INCOMES, GROUP_NAMES.DIRECT_INCOME, 'Indirect Income'];
    const netExpenseGroups = [
      GROUP_NAMES.TRADING_EXPENSE, 
      GROUP_NAMES.DIRECT_EXPENSE, 
      'Indirect Expenses',
      'Depreciation & Amortisation',
      'Interest Expenses',
      'Tax Expenses'
    ];

    const currentGrossIncome = calculateSum(currentData, grossIncomeGroups) + stockData.currentClosingStock;
    const previousGrossIncome = calculateSum(previousData, grossIncomeGroups) + stockData.previousClosingStock;
    const currentGrossExpense = calculateSum(currentData, grossExpenseGroups) + stockData.currentOpeningStock;
    const previousGrossExpense = calculateSum(previousData, grossExpenseGroups) + stockData.previousOpeningStock;

    const currentNetIncome = calculateSum(currentData, netIncomeGroups) + stockData.currentClosingStock;
    const previousNetIncome = calculateSum(previousData, netIncomeGroups) + stockData.previousClosingStock;
    const currentNetExpense = calculateSum(currentData, netExpenseGroups) + stockData.currentOpeningStock;
    const previousNetExpense = calculateSum(previousData, netExpenseGroups) + stockData.previousOpeningStock;

    return {
      currentGrossIncome,
      previousGrossIncome,
      currentGrossExpense,
      previousGrossExpense,
      currentNetIncome,
      previousNetIncome,
      currentNetExpense,
      previousNetExpense,
      currentGross: currentGrossIncome - currentGrossExpense,
      previousGross: previousGrossIncome - previousGrossExpense,
      currentNet: currentNetIncome - currentNetExpense,
      previousNet: previousNetIncome - previousNetExpense,
    };
  }, [currentData, previousData, stockData, calculateSum]);

  const previousDataMap = React.useMemo(() => {
    if (!previousData?.length) return new Map();
    return new Map(previousData.map(item => [item.group_name, item]));
  }, [previousData]);

  const cardData = React.useMemo(() => {
    if (!currentData?.length && !previousData?.length) {
      return { data: { ...INITIAL_CARD_STATE }, currentGross: 0, previousGross: 0 };
    }

    const data = currentData.reduce(
      (acc, currentItem) => {
        const key = GROUP_NAME_TO_KEY_MAP[currentItem.group_name];
        if (!key) return acc;

        const previousItem = previousDataMap.get(currentItem.group_name);

        if (key === 'cashInHand') {
          const isCashInHand = currentItem.group_name === GROUP_NAMES.CASH_IN_HAND;
          const isBankAccount = currentItem.group_name === GROUP_NAMES.BANK_ACCOUNTS;
          
          const currentBalance = (isCashInHand || isBankAccount) ? currentItem.balance : 0;
          const previousBalance = previousItem?.balance || 0;

          acc.cashInHand = {
            current: (acc.cashInHand?.current || 0) + currentBalance,
            previous: (acc.cashInHand?.previous || 0) + previousBalance,
          };
        } else if (key === 'totalIncomes' || key === 'totalExpenses' || key === 'indirectExpenses') {
          acc[key] = {
            current: (acc[key]?.current || 0) + (currentItem.balance || 0),
            previous: (acc[key]?.previous || 0) + (previousItem?.balance || 0),
          };
        } else {
          acc[key] = {
            current: currentItem.balance,
            previous: previousItem?.balance || 0,
          };
        }

        return acc;
      },
      { ...INITIAL_CARD_STATE }
    );

    data.closingStock = {
      current: stockData.currentClosingStock,
      previous: stockData.previousClosingStock,
    };

    data.openingStock = {
      current: stockData.currentOpeningStock,
      previous: stockData.previousOpeningStock,
    };

    data.currentassets.current += stockData.currentClosingStock - stockData.currentOpeningStock;
    data.currentassets.previous += stockData.previousClosingStock - stockData.previousOpeningStock;

    data.netCurrentAssets = {
      current: data.currentassets.current - data.currentliabilities.current - stockData.currentOpeningStock,
      previous: data.currentassets.previous - data.currentliabilities.previous - stockData.previousOpeningStock,
    };

    data.grossProfit = {
      current: incomeExpenseData.currentGross,
      previous: incomeExpenseData.previousGross,
      currentLoss: incomeExpenseData.currentGross < 0,
      previousLoss: incomeExpenseData.previousGross < 0,
    };

    data.netProfit = {
      current: incomeExpenseData.currentNet,
      previous: incomeExpenseData.previousNet,
      currentLoss: incomeExpenseData.currentNet < 0,
      previousLoss: incomeExpenseData.previousNet < 0,
    };

    return { data, currentGross: incomeExpenseData.currentGross, previousGross: incomeExpenseData.previousGross };
  }, [currentData, previousData, previousDataMap, calculateSum, stockData, incomeExpenseData]);

  const calculatePercentageChange = React.useCallback((key, current, previous) => {
    if (!current || !previous || previous === '0.00') {
      return { percentageChange: 0, isPositive: true };
    }

    const currentValue = Math.abs(current);
    const previousValue = Math.abs(previous);
    const percentageChange = ((currentValue - previousValue) / previousValue) * 100;

    return {
      percentageChange: Math.abs(Number(percentageChange.toFixed(2))),
      isPositive: key === 'totalExpenses' ? percentageChange < 0 : percentageChange >= 0,
    };
  }, []);

  const cardItems = React.useMemo(() => {
    return GROUPED_DATA.map(group => 
      group.map(({ key, title }) => {
        const { current = 0, previous = 0, currentLoss = false, previousLoss = false } = cardData.data[key] || {};
        const { percentageChange, isPositive } = calculatePercentageChange(key, current, previous);
        return {
          key,
          title,
          current,
          previous,
          percentageChange,
          isPositive,
          currentLoss,
          previousLoss,
        };
      })
    );
  }, [cardData.data, calculatePercentageChange]);

  const backgroundColors = ["#D1FAE5", "#FEF3C7", "#DBEAFE", "#EDE9FE"];
  const linearBackground = ["linear-gradient(90deg,rgba(2, 217, 202, 1) 0%, rgba(35, 217, 205, 1) 20%, rgba(105, 245, 235, 1) 40%, rgba(148, 247, 241, 1) 60%, rgba(179, 255, 247, 1) 80%, rgba(207, 255, 247, 1) 93%);","linear-gradient(90deg,rgba(224, 180, 0, 1) 0%, rgba(224, 185, 29, 1) 20%, rgba(232, 206, 102, 1) 40%, rgba(237, 218, 140, 1) 60%, rgba(240, 225, 165, 1) 80%, rgba(252, 244, 187, 1) 93%);","linear-gradient(90deg,rgba(31, 150, 255, 1) 0%, rgba(61, 165, 255, 1) 20%, rgba(92, 179, 255, 1) 40%, rgba(135, 199, 255, 1) 60%, rgba(176, 218, 255, 1) 80%, rgba(217, 237, 255, 1) 93%);","linear-gradient(90deg,rgba(204, 196, 255, 1) 50%, rgba(216, 209, 255, 1) 79%, rgba(221, 217, 255, 1) 96%);"]
  const border_color = ["rgba(9, 217, 203, 0.51)","rgba(221, 178, 8, 0.49)","rgba(74, 164, 242, 0.47)","rgba(138, 124, 216, 0.52)"]
  
  
  return (
    <Grid container spacing={1}>
      {cardItems.map((group, index) => (
        <Grid item xs={12} md={6} lg={3} key={index} sx={{ p: 0, m: 0 }}>
          <DataCard
            items={group}
            loading={loading}
            backGroundColor={backgroundColors[index]}
            linearBackground={linearBackground[index]}
            border_color={border_color[index]}
          />
        </Grid>
      ))}
    </Grid>
  );
});

export default ComparisonCard;
